import type { CostMeterResult } from "@/utils/smsCostMeter";

/** The composer's live cost meter, rendered directly beneath the message box
 *  (captain-approved round-4 placement): length as sent, encoding, texts per
 *  person, cost times the audience, and the improve-this-text tips. Pure
 *  display — all math lives in utils/smsCostMeter. */
export function SmsCostMeter({
  meter,
  audienceCount,
  costPerSegment,
}: {
  meter: CostMeterResult;
  audienceCount: number;
  costPerSegment: number;
}) {
  const singleSegmentBudget = meter.encoding === "GSM-7" ? 160 : 70;
  const fillPct =
    meter.segments === 1
      ? Math.min(100, Math.round((meter.charCount / singleSegmentBudget) * 100))
      : 100;
  const overBudget = meter.segments > 1;

  return (
    <div className="mt-3 rounded-lg border border-border bg-background p-3 text-sm" data-testid="sms-cost-meter">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-muted-foreground">Length (as sent, link expanded)</span>
        <span className="font-medium tabular-nums">
          {meter.charCount}
          {meter.segments === 1 ? ` / ${singleSegmentBudget}` : ""}
        </span>
      </div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted" role="presentation">
        <div
          className={`h-full rounded-full ${overBudget ? "bg-status-warning" : "bg-status-success"}`}
          style={{ width: `${fillPct}%` }}
        />
      </div>
      <dl className="mt-2 space-y-1">
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Encoding</dt>
          <dd className={meter.encoding === "UCS-2" ? "font-medium text-status-warning" : "font-medium"}>
            {meter.encoding === "GSM-7" ? "Plain text (GSM-7)" : "Unicode (UCS-2)"}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Texts per person</dt>
          <dd className={`font-medium tabular-nums ${overBudget ? "text-status-warning" : ""}`}>
            {meter.segments} {meter.isMms ? "(sends as MMS)" : meter.segments === 1 ? "SMS" : "segments"}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Cost</dt>
          <dd className="font-medium tabular-nums">
            {audienceCount} × {meter.segments} × ${costPerSegment.toFixed(2)} ≈ ${meter.totalCost.toFixed(2)}
          </dd>
        </div>
      </dl>
      {meter.tips.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {meter.tips.map((tip) => (
            <p
              key={tip.kind}
              className={`rounded-md px-2.5 py-1.5 text-xs leading-relaxed ${
                tip.kind === "headroom"
                  ? "bg-muted text-muted-foreground"
                  : "bg-status-caution/30 text-foreground"
              }`}
            >
              {tip.kind !== "headroom" && <span className="font-semibold">Improve this text: </span>}
              {tip.message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
